import React, { useEffect, useMemo, useRef, useState } from "react";
import "./TeamfightBoard.css";
import TeamfightGrid from "./components/TeamfightGrid";
import ChampionPanel from "./components/ChampionPanel";
import ChampionsList from "./components/ChampionsList";
import type { Unit, Team, Role } from "./components/TeamfightGrid";
import type { SpellKey, SpellDef } from "./components/ChampionPanel";

/**
 * NEW:
 * - Press Q/W/E/R/A to queue spells (combo).
 * - When a combo exists, hover shows target tile; left-click casts next spell at that tile (if in range).
 * - Range check per spell; invalid targets briefly flash.
 * - Simple animations: beams (A) and AoE pulses (Q/W/E/R).
 * - Click spells in ChampionPanel to enqueue them too.
 * - Esc clears current combo. Backspace removes last queued spell.
 * Notes:
 * - Left-click still selects when no combo is armed.
 * - Right-click moves selected (unchanged).
 */

const GRID_SIZE = 20; // Standard grid size
const TILE_SIZE_PX = 20; // 20px tiles  
const CHAMPION_SIZE = 2; // Champions occupy 2x2 tiles (40px x 40px)

// Types are now imported from components

const ROLES: Role[] = ["TOP", "JGL", "MID", "ADC", "SUP"];
const ROLE_DEFAULT_RANGE: Record<Role, number> = { TOP: 2, JGL: 2, MID: 3, ADC: 3, SUP: 2 }; // Melee: 2 squares (40px), Ranged: 3 squares (60px)
const ROLE_DEFAULT_HEALTH: Record<Role, number> = { TOP: 2800, JGL: 2500, MID: 2200, ADC: 1800, SUP: 2000 };
const FORMATIONS = ["Front-to-Back", "Protect-ADC", "Wedge"] as const;
type Formation = typeof FORMATIONS[number];

// Helpers
const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));
const keyXY = (x: number, y: number) => `${x},${y}`;
const dist = (a: { x: number; y: number }, b: { x: number; y: number }) => Math.hypot(a.x - b.x, a.y - b.y);

// Spells per unit - melee roles get 2 range, ranged roles get 3 range
function getSpellsFor(u: Unit): Record<SpellKey, SpellDef> {
  const roleSpells: Record<Role, Record<SpellKey, Omit<SpellDef, "key">>> = {
    TOP: {
      Q: { name: "Slam", range: 2, damage: 180, kind: "aoe", radius: 1.5 },
      W: { name: "Shield", range: 0, damage: 0, kind: "aoe", radius: 1 },
      E: { name: "Charge", range: 2, damage: 150, kind: "beam" },
      R: { name: "Execute", range: 2, damage: 350, kind: "aoe", radius: 1.25 },
      A: { name: "Auto", range: u.range, damage: 120, kind: "beam" },
    },
    JGL: {
      Q: { name: "Pounce", range: 2, damage: 200, kind: "beam" },
      W: { name: "Stealth", range: 0, damage: 0, kind: "aoe", radius: 0.75 },
      E: { name: "Slash", range: 2, damage: 160, kind: "aoe", radius: 1 },
      R: { name: "Hunt", range: 2, damage: 280, kind: "beam" },
      A: { name: "Auto", range: u.range, damage: 110, kind: "beam" },
    },
    MID: {
      Q: { name: "Fireball", range: 3, damage: 220, kind: "aoe", radius: 1.25 },
      W: { name: "Shield", range: 0, damage: 0, kind: "aoe", radius: 1.5 },
      E: { name: "Lightning", range: 3, damage: 180, kind: "beam" },
      R: { name: "Meteor", range: 3, damage: 400, kind: "aoe", radius: 2 },
      A: { name: "Auto", range: u.range, damage: 80, kind: "beam" },
    },
    ADC: {
      Q: { name: "Pierce", range: 3, damage: 160, kind: "beam" },
      W: { name: "Dash", range: 3, damage: 0, kind: "beam" },
      E: { name: "Volley", range: 3, damage: 140, kind: "aoe", radius: 1.5 },
      R: { name: "Rain", range: 3, damage: 320, kind: "aoe", radius: 2.5 },
      A: { name: "Auto", range: u.range, damage: 150, kind: "beam" },
    },
    SUP: {
      Q: { name: "Heal", range: 2, damage: -180, kind: "aoe", radius: 1.25 },
      W: { name: "Shield", range: 2, damage: 0, kind: "beam" },
      E: { name: "Stun", range: 2, damage: 100, kind: "beam" },
      R: { name: "Revive", range: 2, damage: -300, kind: "aoe", radius: 1.75 },
      A: { name: "Auto", range: u.range, damage: 60, kind: "beam" },
    },
  };

  const spells = roleSpells[u.role];
  return Object.fromEntries(
    Object.entries(spells).map(([key, spell]) => [key, { key: key as SpellKey, ...spell }])
  ) as Record<SpellKey, SpellDef>;
}

// Initial placements (positioned for 2x2 champions on 20x20 grid)
function initialUnits(): Unit[] {
  const blues: Unit[] = [
    { id: "BLUE-TOP", team: "BLUE", role: "TOP", x: 2, y: 14, range: ROLE_DEFAULT_RANGE.TOP, health: ROLE_DEFAULT_HEALTH.TOP, maxHealth: ROLE_DEFAULT_HEALTH.TOP },
    { id: "BLUE-JGL", team: "BLUE", role: "JGL", x: 5, y: 15, range: ROLE_DEFAULT_RANGE.JGL, health: ROLE_DEFAULT_HEALTH.JGL, maxHealth: ROLE_DEFAULT_HEALTH.JGL },
    { id: "BLUE-MID", team: "BLUE", role: "MID", x: 8, y: 15, range: ROLE_DEFAULT_RANGE.MID, health: ROLE_DEFAULT_HEALTH.MID, maxHealth: ROLE_DEFAULT_HEALTH.MID },
    { id: "BLUE-ADC", team: "BLUE", role: "ADC", x: 9, y: 17, range: ROLE_DEFAULT_RANGE.ADC, health: ROLE_DEFAULT_HEALTH.ADC, maxHealth: ROLE_DEFAULT_HEALTH.ADC },
    { id: "BLUE-SUP", team: "BLUE", role: "SUP", x: 6, y: 17, range: ROLE_DEFAULT_RANGE.SUP, health: ROLE_DEFAULT_HEALTH.SUP, maxHealth: ROLE_DEFAULT_HEALTH.SUP },
  ];
  const reds: Unit[] = [
    { id: "RED-TOP", team: "RED", role: "TOP", x: 16, y: 4, range: ROLE_DEFAULT_RANGE.TOP, health: ROLE_DEFAULT_HEALTH.TOP, maxHealth: ROLE_DEFAULT_HEALTH.TOP },
    { id: "RED-JGL", team: "RED", role: "JGL", x: 13, y: 3, range: ROLE_DEFAULT_RANGE.JGL, health: ROLE_DEFAULT_HEALTH.JGL, maxHealth: ROLE_DEFAULT_HEALTH.JGL },
    { id: "RED-MID", team: "RED", role: "MID", x: 10, y: 3, range: ROLE_DEFAULT_RANGE.MID, health: ROLE_DEFAULT_HEALTH.MID, maxHealth: ROLE_DEFAULT_HEALTH.MID },
    { id: "RED-ADC", team: "RED", role: "ADC", x: 9, y: 1, range: ROLE_DEFAULT_RANGE.ADC, health: ROLE_DEFAULT_HEALTH.ADC, maxHealth: ROLE_DEFAULT_HEALTH.ADC },
    { id: "RED-SUP", team: "RED", role: "SUP", x: 12, y: 1, range: ROLE_DEFAULT_RANGE.SUP, health: ROLE_DEFAULT_HEALTH.SUP, maxHealth: ROLE_DEFAULT_HEALTH.SUP },
  ];
  return [...blues, ...reds];
}

// Presets (positioned for 2x2 champions on 20x20 grid)
const presetPositions: Record<Formation, Record<Team, Record<Role, { x: number; y: number }>>> = {
  "Front-to-Back": {
    BLUE: { TOP: { x: 6, y: 12 }, JGL: { x: 9, y: 13 }, MID: { x: 9, y: 15 }, ADC: { x: 11, y: 16 }, SUP: { x: 7, y: 16 } },
    RED: { TOP: { x: 12, y: 6 }, JGL: { x: 9, y: 5 }, MID: { x: 9, y: 3 }, ADC: { x: 7, y: 2 }, SUP: { x: 11, y: 2 } },
  },
  "Protect-ADC": {
    BLUE: { TOP: { x: 6, y: 13 }, JGL: { x: 12, y: 13 }, MID: { x: 9, y: 14 }, ADC: { x: 9, y: 16 }, SUP: { x: 7, y: 16 } },
    RED: { TOP: { x: 12, y: 5 }, JGL: { x: 6, y: 5 }, MID: { x: 9, y: 4 }, ADC: { x: 9, y: 2 }, SUP: { x: 11, y: 2 } },
  },
  Wedge: {
    BLUE: { TOP: { x: 8, y: 12 }, JGL: { x: 6, y: 14 }, MID: { x: 10, y: 14 }, ADC: { x: 11, y: 16 }, SUP: { x: 7, y: 16 } },
    RED: { TOP: { x: 10, y: 6 }, JGL: { x: 12, y: 4 }, MID: { x: 8, y: 4 }, ADC: { x: 7, y: 2 }, SUP: { x: 11, y: 2 } },
  },
};

// Effect visuals
type Effect =
  | { id: number; kind: "aoe"; x: number; y: number; radius: number; team: Team; duration: number }
  | { id: number; kind: "beam"; x1: number; y1: number; x2: number; y2: number; team: Team; duration: number };

export default function TeamfightBoard() {
  const [units, setUnits] = useState<Unit[]>(() => initialUnits());
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [targetId, setTargetId] = useState<string | null>(null);
  const [showRanges, setShowRanges] = useState(false);
  const [activeFormationBlue, setActiveFormationBlue] = useState<Formation>("Front-to-Back");
  const [activeFormationRed, setActiveFormationRed] = useState<Formation>("Front-to-Back");
  const [showCoords, setShowCoords] = useState(false);

  // Targeting & effects
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null);
  const [spellHistory, setSpellHistory] = useState<{spellKey: SpellKey, timestamp: number}[]>([]);
  const [effects, setEffects] = useState<Effect[]>([]);
  const [invalidFlashAt, setInvalidFlashAt] = useState<{ x: number; y: number } | null>(null);
  const effectId = useRef(1);

  // Create occupancy map for collision detection in movement
  const occ = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of units) {
      // Champions occupy 2x2 tiles
      for (let dx = 0; dx < CHAMPION_SIZE; dx++) {
        for (let dy = 0; dy < CHAMPION_SIZE; dy++) {
          m.set(keyXY(u.x + dx, u.y + dy), u.id);
        }
      }
    }
    return m;
  }, [units]);

  const selectedUnit = useMemo(() => units.find((u) => u.id === selectedId) || null, [units, selectedId]);
  const targetUnit = useMemo(() => units.find((u) => u.id === targetId) || null, [units, targetId]);
  const spells = useMemo(() => (selectedUnit ? getSpellsFor(selectedUnit) : null), [selectedUnit]);
  const hoveredUnit = useMemo(() => {
    if (!hover) return null;
    // Check if hover position is within any unit's 2x2 footprint
    return units.find((u) => 
      hover.x >= u.x && hover.x < u.x + CHAMPION_SIZE && 
      hover.y >= u.y && hover.y < u.y + CHAMPION_SIZE
    ) || null;
  }, [units, hover]);

  // Keyboard: movement + immediate spell casting
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      const activeTag = (e.target as HTMLElement)?.tagName;
      const inInput = activeTag === "INPUT" || activeTag === "TEXTAREA";
      if (!selectedUnit) return;

      // Movement
      const dir: Record<string, [number, number]> = {
        ArrowUp: [0, -1], ArrowDown: [0, 1], ArrowLeft: [-1, 0], ArrowRight: [1, 0],
      };
      if (dir[e.key]) {
        e.preventDefault();
        moveBy(selectedUnit, dir[e.key][0], dir[e.key][1]);
        return;
      }

      if (inInput) return;

      // Spell keys - cast immediately at hovered target
      const k = e.key.toUpperCase();
      if (["Q", "W", "E", "R", "A"].includes(k)) {
        e.preventDefault();
        castSpellImmediately(k as SpellKey);
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        setTargetId(null);
        return;
      }
    }
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUnit, units, hover, hoveredUnit]);

  // Left click: select any unit (regardless of team)
  function onLeftClickTile(x: number, y: number) {
    const clickedUnitId = occ.get(keyXY(x, y));
    if (clickedUnitId) {
      if (selectedId === clickedUnitId) {
        // Clicking same unit - do nothing
        return;
      } else {
        // Always select the clicked unit, regardless of team
        setSelectedId(clickedUnitId);
        setTargetId(null);
      }
    } else {
      // Clicking empty space - clear target
      setTargetId(null);
    }
  }

  // Right click: move selected if empty, or move to attack range of target
  function onRightClickTile(e: React.MouseEvent, x: number, y: number) {
    e.preventDefault();
    if (!selectedUnit) return;
    
    const clickedUnitId = occ.get(keyXY(x, y));
    
    if (clickedUnitId && clickedUnitId !== selectedUnit.id) {
      // Right-clicked on another champion - move to attack range
      const targetChampion = units.find(u => u.id === clickedUnitId);
      if (targetChampion) {
        moveToAttackRange(selectedUnit, targetChampion);
      }
    } else {
      // Right-clicked on empty space - move there
      const nx = clamp(x, 0, GRID_SIZE - CHAMPION_SIZE);
      const ny = clamp(y, 0, GRID_SIZE - CHAMPION_SIZE);
      
      // Check if new position would collide with other units
      const wouldCollide = units.some(other => {
        if (other.id === selectedUnit.id) return false;
        return (
          nx < other.x + CHAMPION_SIZE && nx + CHAMPION_SIZE > other.x &&
          ny < other.y + CHAMPION_SIZE && ny + CHAMPION_SIZE > other.y
        );
      });
      
      if (!wouldCollide) {
        setUnits((prev) => prev.map((u) => (u.id === selectedUnit.id ? { ...u, x: nx, y: ny } : u)));
      }
    }
  }

  function moveBy(u: Unit, dx: number, dy: number) {
    const nx = clamp(u.x + dx, 0, GRID_SIZE - CHAMPION_SIZE);
    const ny = clamp(u.y + dy, 0, GRID_SIZE - CHAMPION_SIZE);
    
    // Check if new position would collide with other units
    const wouldCollide = units.some(other => {
      if (other.id === u.id) return false;
      return (
        nx < other.x + CHAMPION_SIZE && nx + CHAMPION_SIZE > other.x &&
        ny < other.y + CHAMPION_SIZE && ny + CHAMPION_SIZE > other.y
      );
    });
    
    if (wouldCollide) return;
    setUnits((prev) => prev.map((it) => (it.id === u.id ? { ...it, x: nx, y: ny } : it)));
  }

  function moveToAttackRange(attacker: Unit, target: Unit) {
    // Find the closest position to the target that's within attack range
    const range = attacker.range;
    let bestPosition: { x: number; y: number } | null = null;
    let bestDistance = Infinity;

    // Check all positions around the target
    for (let dx = -range - 1; dx <= range + 1; dx++) {
      for (let dy = -range - 1; dy <= range + 1; dy++) {
        const newX = target.x + dx;
        const newY = target.y + dy;
        
        // Check if position is within grid bounds (accounting for 2x2 size)
        if (newX < 0 || newX >= GRID_SIZE - CHAMPION_SIZE + 1 || newY < 0 || newY >= GRID_SIZE - CHAMPION_SIZE + 1) continue;
        
        // Check if position would collide with another unit
        const wouldCollide = units.some(other => {
          if (other.id === attacker.id) return false;
          return (
            newX < other.x + CHAMPION_SIZE && newX + CHAMPION_SIZE > other.x &&
            newY < other.y + CHAMPION_SIZE && newY + CHAMPION_SIZE > other.y
          );
        });
        if (wouldCollide) continue;
        
        // Check if this position is within attack range of target
        const distance = Math.sqrt(Math.pow(newX - target.x, 2) + Math.pow(newY - target.y, 2));
        if (distance <= range + 0.5) { // +0.5 for center-to-center calculation
          // Find closest valid position to current attacker position
          const distanceToAttacker = Math.sqrt(Math.pow(newX - attacker.x, 2) + Math.pow(newY - attacker.y, 2));
          if (distanceToAttacker < bestDistance) {
            bestDistance = distanceToAttacker;
            bestPosition = { x: newX, y: newY };
          }
        }
      }
    }

    if (bestPosition) {
      setUnits((prev) => prev.map((u) => 
        u.id === attacker.id ? { ...u, x: bestPosition!.x, y: bestPosition!.y } : u
      ));
    }
  }

  function resetPositions() {
    setUnits(initialUnits());
    setSelectedId(null);
    setTargetId(null);
    setSpellHistory([]);
  }

  function flipSides() {
    setUnits((prev) => prev.map((u) => ({ ...u, x: GRID_SIZE - 1 - u.x, y: GRID_SIZE - 1 - u.y })));
  }

  function applyFormation(team: Team, formation: Formation) {
    setUnits((prev) => {
      const map = presetPositions[formation][team];
      return prev.map((u) => {
        if (u.team !== team) return u;
        const pos = map[u.role];
        if (!pos) return u;
        const collision = prev.some((p) => p.id !== u.id && p.team === team && p.x === pos.x && p.y === pos.y);
        const nx = clamp(collision ? pos.x + 1 : pos.x, 0, GRID_SIZE - 1);
        return { ...u, x: nx, y: pos.y };
      });
    });
  }

  function setRange(u: Unit, range: number) {
    setUnits((prev) => prev.map((it) => (it.id === u.id ? { ...it, range } : it)));
  }

  // Selected range highlight - circular range measured from champion center
  const inRangeTiles = useMemo(() => {
    if (!showRanges || !selectedUnit) return new Set<string>();
    const S = new Set<string>();
    const championCenterX = selectedUnit.x + CHAMPION_SIZE / 2;
    const championCenterY = selectedUnit.y + CHAMPION_SIZE / 2;
    
    for (let y = 0; y < GRID_SIZE; y++) {
      for (let x = 0; x < GRID_SIZE; x++) {
        // Calculate distance from champion center to tile center
        const tileCenterX = x + 0.5;
        const tileCenterY = y + 0.5;
        const distance = Math.sqrt(
          Math.pow(tileCenterX - championCenterX, 2) + 
          Math.pow(tileCenterY - championCenterY, 2)
        );
        // Subtract champion radius to get distance from edge
        const distanceFromEdge = distance - (CHAMPION_SIZE / 2);
        if (distanceFromEdge <= selectedUnit.range) S.add(keyXY(x, y));
      }
    }
    return S;
  }, [selectedUnit, showRanges]);

  function castSpellImmediately(spellKey: SpellKey) {
    if (!selectedUnit || !spells) return;
    
    const def = spells[spellKey];
    let targetX: number, targetY: number;
    
    // Determine target location
    if (hoveredUnit) {
      // Cast at hovered unit
      targetX = hoveredUnit.x;
      targetY = hoveredUnit.y;
      setTargetId(hoveredUnit.id);
    } else if (targetUnit) {
      // Cast at current target
      targetX = targetUnit.x;
      targetY = targetUnit.y;
    } else if (hover) {
      // Cast at hovered tile
      targetX = hover.x;
      targetY = hover.y;
    } else {
      // No target - cast at own position for self-cast spells
      targetX = selectedUnit.x;
      targetY = selectedUnit.y;
    }

    // Calculate circular distance from champion center to target
    const championCenterX = selectedUnit.x + CHAMPION_SIZE / 2;
    const championCenterY = selectedUnit.y + CHAMPION_SIZE / 2;
    const targetCenterX = targetX + 0.5;
    const targetCenterY = targetY + 0.5;
    const distance = Math.sqrt(
      Math.pow(targetCenterX - championCenterX, 2) + 
      Math.pow(targetCenterY - championCenterY, 2)
    );
    const distanceFromEdge = distance - (CHAMPION_SIZE / 2);
    if (distanceFromEdge > def.range) {
      // invalid target feedback
      setInvalidFlashAt({ x: targetX, y: targetY });
      setTimeout(() => setInvalidFlashAt((curr) => (curr && curr.x === targetX && curr.y === targetY ? null : curr)), 150);
      return;
    }

    // Find targets and apply damage
    let targets: Unit[] = [];
    if (def.kind === "aoe" && def.radius) {
      targets = findTargetsInRange(targetX, targetY, def.radius, selectedUnit.team);
    } else if (def.kind === "beam") {
      const targetAtLocation = units.find((u) => u.x === targetX && u.y === targetY);
      if (targetAtLocation) targets = [targetAtLocation];
    }

    if (targets.length > 0 && def.damage !== 0) {
      applyDamage(targets.map(t => t.id), def.damage, selectedUnit.team);
    }

    // Add to spell history
    setSpellHistory(prev => [...prev.slice(-4), { spellKey, timestamp: Date.now() }]);

    // spawn effect
    const id = effectId.current++;
    if (def.kind === "aoe") {
      setEffects((E) => [...E, { id, kind: "aoe", x: targetX, y: targetY, radius: def.radius ?? 0.8, team: selectedUnit.team, duration: 450 }]);
      setTimeout(() => setEffects((E) => E.filter((e) => e.id !== id)), 450);
    } else if (def.kind === "beam") {
      setEffects((E) => [...E, { id, kind: "beam", x1: selectedUnit.x, y1: selectedUnit.y, x2: targetX, y2: targetY, team: selectedUnit.team, duration: 280 }]);
      setTimeout(() => setEffects((E) => E.filter((e) => e.id !== id)), 280);
    }
  }

  function findTargetsInRange(centerX: number, centerY: number, radius: number, casterTeam: Team): Unit[] {
    return units.filter((u) => {
      const distance = dist({ x: centerX, y: centerY }, { x: u.x, y: u.y });
      return distance <= radius + 1e-9;
    });
  }

  function applyDamage(targetIds: string[], damage: number, casterTeam: Team) {
    setUnits((prev) => 
      prev.map((u) => {
        if (!targetIds.includes(u.id)) return u;
        
        // Healing (negative damage) can target same team
        if (damage < 0 && u.team !== casterTeam) return u;
        // Damage can only target enemy team
        if (damage > 0 && u.team === casterTeam) return u;
        
        const newHealth = Math.max(0, Math.min(u.maxHealth, u.health - damage));
        return { ...u, health: newHealth };
      })
    );
  }

  return (
    <div className="tf-page">
      <div className="tf-container">
        {/* Sidebar */}
        <aside className="tf-sidebar">
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>LoL Teamfight Grid</h1>
          <p style={{ margin: 0, opacity: 0.8, fontSize: 12 }}>
            20×20 board. Left-click to select. Right-click to move or auto-range. Press Q/W/E/R/A to cast spells.
          </p>

          {/* Champion Panel */}
          <ChampionPanel 
            unit={selectedUnit} 
            target={targetUnit} 
            spells={spells}
            onClickSpell={(k) => castSpellImmediately(k)} 
          />

          {/* Spell History */}
          <div className="tf-card">
            <div className="tf-section-title">Recent Spells</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
              {spellHistory.length === 0 ? (
                <div style={{ fontSize: 12, opacity: 0.6 }}>No spells cast yet</div>
              ) : (
                spellHistory.map((spell, i) => (
                  <div key={`${spell.timestamp}-${i}`} className="tf-pill">
                    {spell.spellKey}
                  </div>
                ))
              )}
              <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                <button className="tf-btn" onClick={() => setSpellHistory([])}>Clear History</button>
              </div>
            </div>
            <div style={{ marginTop: 6, fontSize: 12, opacity: 0.7 }}>
              Tip: press Q/W/E/R/A to cast spells immediately at hovered target.
            </div>
          </div>

          {/* Toggles */}
          <div className="tf-card">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={showRanges} onChange={(e) => setShowRanges(e.target.checked)} />
                Show selected range
              </label>
              <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 14 }}>
                <input type="checkbox" checked={showCoords} onChange={(e) => setShowCoords(e.target.checked)} />
                Show tile coordinates
              </label>
            </div>
          </div>

          {/* Formations */}
          <div className="tf-card">
            <h2 className="tf-section-title">Formations</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ width: 36, fontSize: 12, opacity: 0.8 }}>Blue</span>
                <select className="tf-select" value={activeFormationBlue} onChange={(e) => setActiveFormationBlue(e.target.value as Formation)}>
                  {FORMATIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <button className="tf-btn tf-btn--blue" style={{ marginLeft: "auto" }} onClick={() => applyFormation("BLUE", activeFormationBlue)}>
                  Apply
                </button>
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ width: 36, fontSize: 12, opacity: 0.8 }}>Red</span>
                <select className="tf-select" value={activeFormationRed} onChange={(e) => setActiveFormationRed(e.target.value as Formation)}>
                  {FORMATIONS.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
                <button className="tf-btn tf-btn--red" style={{ marginLeft: "auto" }} onClick={() => applyFormation("RED", activeFormationRed)}>
                  Apply
                </button>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <button className="tf-btn" onClick={resetPositions}>Reset</button>
              <button className="tf-btn" onClick={flipSides}>Flip Sides</button>
            </div>
          </div>

          {/* Units */}
          <ChampionsList
            units={units}
            selectedId={selectedId}
            onSelectUnit={setSelectedId}
            onSetRange={setRange}
          />

          <div style={{ fontSize: 12, opacity: 0.7 }}>
            <p style={{ margin: 0 }}>Shortcuts:</p>
            <ul className="list" style={{ marginTop: 4 }}>
              <li>Q/W/E/R/A: cast spell immediately at hovered target</li>
              <li>Left click: select any champion (any team)</li>
              <li>Right click: move to position or auto-range champion</li>
              <li>Arrow keys: step movement • Esc: clear target</li>
            </ul>
          </div>
        </aside>

        {/* Board */}
        <main>
          <TeamfightGrid
            units={units}
            selectedId={selectedId}
            targetId={targetId}
            showRanges={showRanges}
            showCoords={showCoords}
            effects={effects}
            hover={hover}
            invalidFlashAt={invalidFlashAt}
            inRangeTiles={inRangeTiles}
            onLeftClickTile={onLeftClickTile}
            onRightClickTile={onRightClickTile}
            onHover={(x, y) => setHover({ x, y })}
          />
        </main>
      </div>
    </div>
  );
}

// Component definitions moved to separate files
